// src/screens/RegistroProveedorScreen.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebaseConfig';
import { doc, setDoc, updateDoc, getDoc, Timestamp, collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import {
    IoArrowBack,
    IoCloudUploadOutline,
    IoTimeOutline,
    IoLocationOutline,
    IoDocumentTextOutline,
    IoBriefcaseOutline,
    IoPersonOutline,
    IoMapOutline,
    IoAlertCircleOutline,
    IoCheckmarkCircle
} from 'react-icons/io5';
import './RegistroProveedorScreen.css';



function RegistroProveedorScreen() {
    const navigate = useNavigate();
    const { currentUser, userData } = useAuth();

    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [error, setError] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    // --- ESTADOS PARA BÚSQUEDA Y AUTOCOMPLETADO DE DIRECCIÓN (ESTILO GOOGLE MAPS) ---
    const [searchQuery, setSearchQuery] = useState('');
    const [searchLimitExceeded, setSearchLimitExceeded] = useState(false);
    const searchCountRef = useRef(0);

    // --- ESTADOS PARA MODAL DE CONFIRMACIÓN PREMIUM ---
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({
        title: '',
        message: '',
        type: 'success',
        onConfirm: null
    });

    const showCustomAlert = (title, message, type = 'success', onConfirm = null) => {
        setModalConfig({ title, message, type, onConfirm });
        setShowModal(true);
    };

    // --- ESTADO DEL FORMULARIO ---
    const [formData, setFormData] = useState({
        nombrePublico: '',
        categoria: 'Plomería',
        descripcion: '',
        costoVisita: '',
        rangoPrecios: '$',
        especialidadesInput: '',
        rfc: '', // Nuevo campo de control fiscal
        radioCobertura: 15, // Nuevo campo cobertura en km (default 15 km)

        // Contacto
        nombreContacto: '',
        telefonoWhatsapp: '',
        sitioWeb: '',
        facebook: '',
        instagram: '',

        // Ubicación
        tieneLocal: false,
        calle: '',
        numeroExterior: '',
        colonia: '',
        codigoPostal: '',
        ciudad: '',
        estado: '',
    });

    const [scheduleEnabled, setScheduleEnabled] = useState(false);
    const [scheduleStart, setScheduleStart] = useState('09:00');
    const [scheduleEnd, setScheduleEnd] = useState('18:00');
    const [scheduleDays, setScheduleDays] = useState([1, 2, 3, 4, 5]); // 1 = Lunes, 7 = Domingo

    const [docs, setDocs] = useState({
        constanciaFiscal: null,
        identificacionOficial: null,
        fotosGaleria: null
    });

    const [existingDocs, setExistingDocs] = useState({
        constanciaFiscalUrl: null,
        identificacionOficialUrl: null,
        fotosGaleriaUrls: []
    });

    const [categoriasBase, setCategoriasBase] = useState([]);

    // --- LEAFLET MAP & GEOCODING FOR COVERAGE AREA ---
    const mapRef = useRef(null);
    const circleRef = useRef(null);
    const markerRef = useRef(null);
    const [coords, setCoords] = useState({ lat: 20.6719, lng: -103.4162 }); // Default Zapopan

    // Cargar Leaflet dinámicamente
    useEffect(() => {
        if (window.L) return;
        
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);

        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => {
            window.dispatchEvent(new Event('leaflet-loaded'));
        };
        document.body.appendChild(script);
    }, []);

    // --- GOOGLE MAPS AUTOCOMPLETE & GEOCODING CON PROTECCIÓN DE CUOTAS ---
    const activeAddressRef = useRef('');
    const autocompleteRef = useRef(null);

    // Cargar el SDK de Google Maps dinámicamente con las librerías necesarias (Places)
    useEffect(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
            window.dispatchEvent(new Event('google-maps-loaded'));
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY || ''}&libraries=places&language=es`;
        script.async = true;
        script.onload = () => {
            window.dispatchEvent(new Event('google-maps-loaded'));
        };
        document.body.appendChild(script);
    }, []);

    // Inicializar Google Autocomplete en el input con control de cuotas
    useEffect(() => {
        if (fetchingData) return;

        const initGoogleAutocomplete = () => {
            const google = window.google;
            if (!google || !google.maps || !google.maps.places) return;

            const input = document.getElementById('search-address-input');
            if (!input) return;

            if (autocompleteRef.current) return;

            autocompleteRef.current = new google.maps.places.Autocomplete(input, {
                componentRestrictions: { country: 'mx' },
                fields: ['address_components', 'geometry', 'formatted_address'],
                types: ['address']
            });

            autocompleteRef.current.addListener('place_changed', () => {
                if (searchCountRef.current >= 6) {
                    setSearchLimitExceeded(true);
                    alert("¡Límite de búsquedas de dirección alcanzado en esta sesión! Para proteger tu facturación de Google, hemos pausado las llamadas. Aún puedes mover el pin libremente en el mapa o escribir la dirección directamente.");
                    return;
                }
                searchCountRef.current += 1;

                const place = autocompleteRef.current.getPlace();
                if (!place.geometry || !place.geometry.location) return;

                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();

                setCoords({ lat, lng });

                let calle = '';
                let numero = '';
                let colonia = '';
                let ciudad = '';
                let estado = '';
                let cp = '';

                if (place.address_components) {
                    for (const component of place.address_components) {
                        const types = component.types;
                        if (types.includes('route')) {
                            calle = component.long_name;
                        } else if (types.includes('street_number')) {
                            numero = component.long_name;
                        } else if (types.includes('sublocality_level_1') || types.includes('sublocality') || types.includes('neighborhood')) {
                            colonia = component.long_name;
                        } else if (types.includes('locality')) {
                            ciudad = component.long_name;
                        } else if (types.includes('administrative_area_level_1')) {
                            estado = component.long_name;
                        } else if (types.includes('postal_code')) {
                            cp = component.long_name;
                        }
                    }
                }

                const calleCompleta = `${calle} ${numero}`.trim();

                setFormData(prev => ({
                    ...prev,
                    calle: calleCompleta,
                    colonia: colonia,
                    ciudad: ciudad,
                    estado: estado,
                    codigoPostal: cp
                }));

                activeAddressRef.current = place.formatted_address;
                setSearchQuery(place.formatted_address);
            });
        };

        if (window.google && window.google.maps && window.google.maps.places) {
            initGoogleAutocomplete();
        } else {
            window.addEventListener('google-maps-loaded', initGoogleAutocomplete);
        }

        return () => {
            window.removeEventListener('google-maps-loaded', initGoogleAutocomplete);
        };
    }, [fetchingData]);

    // Geocodificación inversa (Mapa a Campos) con Google Maps y protección de cuota
    const reverseGeocode = (lat, lng) => {
        const google = window.google;
        if (!google || !google.maps) return;

        if (searchCountRef.current >= 8) {
            setSearchLimitExceeded(true);
            return;
        }
        searchCountRef.current += 1;

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const place = results[0];
                
                let calle = '';
                let numero = '';
                let colonia = '';
                let ciudad = '';
                let estado = '';
                let cp = '';

                if (place.address_components) {
                    for (const component of place.address_components) {
                        const types = component.types;
                        if (types.includes('route')) {
                            calle = component.long_name;
                        } else if (types.includes('street_number')) {
                            numero = component.long_name;
                        } else if (types.includes('sublocality_level_1') || types.includes('sublocality') || types.includes('neighborhood')) {
                            colonia = component.long_name;
                        } else if (types.includes('locality')) {
                            ciudad = component.long_name;
                        } else if (types.includes('administrative_area_level_1')) {
                            estado = component.long_name;
                        } else if (types.includes('postal_code')) {
                            cp = component.long_name;
                        }
                    }
                }

                const calleCompleta = `${calle} ${numero}`.trim() || place.formatted_address.split(',')[0];

                setFormData(prev => ({
                    ...prev,
                    calle: calleCompleta,
                    colonia: colonia,
                    ciudad: ciudad,
                    estado: estado,
                    codigoPostal: cp
                }));

                activeAddressRef.current = place.formatted_address;
                setSearchQuery(place.formatted_address);
            }
        });
    };

    // Sincronización del Pin al escribir en los campos de dirección manual (con debounce y protección de cuota)
    useEffect(() => {
        const geocodeAddressManual = () => {
            const google = window.google;
            if (!google || !google.maps) return;

            let addressQuery = '';
            if (formData.tieneLocal) {
                if (formData.calle && formData.ciudad) {
                    addressQuery = `${formData.calle}, ${formData.colonia || ''}, ${formData.ciudad}, ${formData.estado || ''}, México`;
                }
            } else {
                if (userData?.condominioId && userData.condominioId !== 'GLOBAL') {
                    return;
                }
            }

            if (!addressQuery) return;

            if (addressQuery === activeAddressRef.current) {
                return;
            }

            if (searchCountRef.current >= 8) {
                setSearchLimitExceeded(true);
                return;
            }
            searchCountRef.current += 1;

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address: addressQuery }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const loc = results[0].geometry.location;
                    setCoords({ lat: loc.lat(), lng: loc.lng() });

                    activeAddressRef.current = results[0].formatted_address;
                    setSearchQuery(results[0].formatted_address);
                }
            });
        };

        const timer = setTimeout(geocodeAddressManual, 1500);
        return () => clearTimeout(timer);
    }, [formData.tieneLocal, formData.calle, formData.colonia, formData.ciudad, formData.estado, formData.codigoPostal]);

    // 1. Inicializar el mapa una sola vez (cuando terminen de cargar los datos de Firestore)
    useEffect(() => {
        let active = true;

        const initializeMap = () => {
            if (!active || fetchingData) return;
            const L = window.L;
            if (!L) return;

            const container = document.getElementById('map-cobertura');
            if (!container) return;

            // Ocultar loading
            const overlay = container.querySelector('.map-loading-overlay');
            if (overlay) overlay.style.display = 'none';

            if (!mapRef.current) {
                // Si el contenedor tiene un ID de Leaflet previo pero la referencia de React está vacía (común en React Dev Mode o hot-reloads), limpiamos el contenedor
                if (container._leaflet_id) {
                    container._leaflet_id = null;
                    container.innerHTML = '';
                }

                mapRef.current = L.map('map-cobertura').setView([coords.lat, coords.lng], 11);
                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '© OpenStreetMap contributors'
                }).addTo(mapRef.current);

                // Crear marcador premium arrastrable
                markerRef.current = L.marker([coords.lat, coords.lng], { draggable: true }).addTo(mapRef.current);

                // Evento al terminar de arrastrar el pin
                markerRef.current.on('dragend', (e) => {
                    const position = e.target.getLatLng();
                    setCoords({ lat: position.lat, lng: position.lng });
                    reverseGeocode(position.lat, position.lng);
                });

                // Registrar evento de clic en el mapa para posicionar el pin
                mapRef.current.on('click', (e) => {
                    const { lat, lng } = e.latlng;
                    setCoords({ lat, lng });
                    reverseGeocode(lat, lng);
                });

                // Crear círculo de cobertura
                const radiusInMeters = Number(formData.radioCobertura || 15) * 1000;
                circleRef.current = L.circle([coords.lat, coords.lng], {
                    color: '#ec4899',
                    fillColor: '#f472b6',
                    fillOpacity: 0.15,
                    radius: radiusInMeters
                }).addTo(mapRef.current);

                try {
                    mapRef.current.fitBounds(circleRef.current.getBounds());
                } catch (e) {
                    // Prevenir error menor si el círculo no tiene límites válidos inmediatamente
                }
            }
        };

        if (!window.L) {
            window.addEventListener('leaflet-loaded', initializeMap);
        } else {
            initializeMap();
        }

        return () => {
            active = false;
            window.removeEventListener('leaflet-loaded', initializeMap);
        };
    }, [fetchingData]);

    // Desmontar recursos del mapa únicamente cuando la pantalla se desmonte por completo
    useEffect(() => {
        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
                circleRef.current = null;
                markerRef.current = null;
            }
        };
    }, []);

    // 2. Actualizar dinámicamente marcador, círculo y vista sin destruir el mapa
    useEffect(() => {
        const L = window.L;
        if (!L || !mapRef.current) return;

        // Actualizar posición del marcador
        if (markerRef.current) {
            markerRef.current.setLatLng([coords.lat, coords.lng]);
        }

        // Actualizar posición y radio del círculo
        const radiusInMeters = Number(formData.radioCobertura || 15) * 1000;
        if (circleRef.current) {
            circleRef.current.setLatLng([coords.lat, coords.lng]);
            circleRef.current.setRadius(radiusInMeters);
        }

        // Mover vista del mapa suavemente al nuevo centro
        mapRef.current.setView([coords.lat, coords.lng]);

        try {
            if (circleRef.current) {
                mapRef.current.fitBounds(circleRef.current.getBounds(), { animate: true, duration: 0.5 });
            }
        } catch (e) {
            // Silenciar errores de límites geométricos
        }
    }, [coords, formData.radioCobertura]);

    useEffect(() => {
        const q = query(collection(db, 'marketplace_categorias'), where('activo', '==', true));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = [];
            snapshot.forEach(doc => data.push({ idDoc: doc.id, ...doc.data() }));
            data.sort((a, b) => (a.orden || 0) - (b.orden || 0));
            setCategoriasBase(data.map(cat => cat.nombre));
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const fetchProviderData = async () => {
            const providerId = userData?.proveedorId || currentUser?.uid;

            if (providerId) {
                try {
                    const docRef = doc(db, "market_proveedores", providerId);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setIsEditing(true);

                        setFormData({
                            nombrePublico: data.nombrePublico || '',
                            categoria: data.categoria || 'Plomería',
                            descripcion: data.descripcion || '',
                            costoVisita: data.costoVisita || '',
                            rangoPrecios: data.rangoPrecios || '$',
                            especialidadesInput: data.especialidades ? data.especialidades.join(', ') : '',
                            rfc: data.rfc || '',
                            radioCobertura: data.radioCobertura || 15,

                            nombreContacto: data.nombreContacto || '',
                            telefonoWhatsapp: data.telefonoWhatsapp || '',
                            sitioWeb: data.sitioWeb || '',
                            facebook: data.redesSociales?.facebook || '',
                            instagram: data.redesSociales?.instagram || '',

                            tieneLocal: data.ubicacion?.tipo === 'local',
                            calle: data.ubicacion?.calle || '',
                            numeroExterior: data.ubicacion?.numeroExterior || '',
                            colonia: data.ubicacion?.colonia || '',
                            codigoPostal: data.ubicacion?.codigoPostal || '',
                            ciudad: data.ubicacion?.ciudad || '',
                            estado: data.ubicacion?.estado || '',
                        });

                        if (data.horario) {
                            if (data.horario.hasOwnProperty('habilitado') || data.horario.hasOwnProperty('horaInicio')) {
                                setScheduleEnabled(data.horario.habilitado || false);
                                setScheduleStart(data.horario.horaInicio || '09:00');
                                setScheduleEnd(data.horario.horaFin || '18:00');
                                setScheduleDays(data.horario.dias || [1, 2, 3, 4, 5]);
                            } else {
                                const oldDays = data.horario;
                                const daysMap = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
                                const activeDays = [];
                                let start = '09:00';
                                let end = '18:00';
                                let hasActive = false;
                                daysMap.forEach((dayName, index) => {
                                    const dayVal = index + 1;
                                    const config = oldDays[dayName] || oldDays[dayName.normalize("NFD").replace(/[\u0300-\u036f]/g, "")];
                                    if (config && config.activo) {
                                        activeDays.push(dayVal);
                                        if (!hasActive) {
                                            start = config.inicio1 || '09:00';
                                            end = config.fin1 || '18:00';
                                            hasActive = true;
                                        }
                                    }
                                });
                                setScheduleEnabled(activeDays.length > 0);
                                setScheduleStart(start);
                                setScheduleEnd(end);
                                setScheduleDays(activeDays.length > 0 ? activeDays : [1, 2, 3, 4, 5]);
                            }
                        }

                        if (data.documentos) {
                            setExistingDocs({
                                constanciaFiscalUrl: data.documentos.constanciaFiscalUrl,
                                identificacionOficialUrl: data.documentos.identificacionOficialUrl,
                                fotosGaleriaUrls: data.fotosGaleria || []
                            });
                        }
                    }
                } catch (err) {
                    console.error("Error fetching provider data:", err);
                }
            }
            setFetchingData(false);
        };

        if (currentUser) {
            fetchProviderData();
        }
    }, [currentUser, userData]);


    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({ ...formData, [e.target.name]: value });
    };

    const handleFileChange = (e, docName) => {
        if (docName === 'fotosGaleria') {
            if (e.target.files && e.target.files.length > 0) {
                // Validar tamaño máximo de cada foto (5MB)
                for (let file of e.target.files) {
                    if (file.size > 5 * 1024 * 1024) {
                        showCustomAlert("Tamaño excedido", `La foto "${file.name}" supera el límite de 5MB permitidos.`, "error");
                        return;
                    }
                }
                setDocs({ ...docs, [docName]: e.target.files });
            }
        } else {
            const file = e.target.files[0];
            if (file) {
                // Validar tamaño máximo (5MB)
                if (file.size > 5 * 1024 * 1024) {
                    showCustomAlert("Archivo muy grande", "El archivo supera el límite máximo permitido de 5MB.", "error");
                    return;
                }
                // Validar formato Mime
                const formatList = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
                if (!formatList.includes(file.type)) {
                    showCustomAlert("Formato no válido", "Solo se admiten archivos PDF o imágenes (PNG, JPG).", "error");
                    return;
                }
                setDocs({ ...docs, [docName]: file });
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // ── CONTROLES DE SEGURIDAD Y FORMATO ──
        if (!formData.nombrePublico || !formData.descripcion || !formData.costoVisita) {
            setError("Por favor completa los datos básicos del negocio.");
            window.scrollTo(0, 0);
            return;
        }

        // Validar RFC
        if (formData.rfc) {
            const rfcRegex = /^[A-Z&Ññ]{3,4}[0-9]{6}[A-Z0-9]{3}$/i;
            if (!rfcRegex.test(formData.rfc)) {
                setError("El RFC ingresado no tiene un formato fiscal válido.");
                window.scrollTo(0, 0);
                return;
            }
        }

        // Validar rango costo visita
        const costoVal = Number(formData.costoVisita);
        if (costoVal < 50 || costoVal > 5000) {
            setError("El costo base de visita debe ser un valor razonable entre $50 y $5,000 pesos.");
            window.scrollTo(0, 0);
            return;
        }

        if (!formData.nombreContacto || !formData.telefonoWhatsapp) {
            setError("El nombre de contacto y WhatsApp son obligatorios.");
            window.scrollTo(0, 0);
            return;
        }

        // Validar número de WhatsApp de 10 dígitos
        const waClean = formData.telefonoWhatsapp.replace(/\D/g, '');
        if (waClean.length !== 10) {
            setError("El número de WhatsApp de negocio debe constar exactamente de 10 dígitos numéricos.");
            window.scrollTo(0, 0);
            return;
        }

        // Validar dirección si tiene local
        if (formData.tieneLocal) {
            if (!formData.calle || !formData.ciudad || !formData.colonia || !formData.codigoPostal) {
                setError("Si tienes local, debes completar la dirección física (Calle, Colonia, Ciudad y CP).");
                window.scrollTo(0, 0);
                return;
            }
            const cpRegex = /^[0-9]{5}$/;
            if (!cpRegex.test(formData.codigoPostal)) {
                setError("El Código Postal debe constar exactamente de 5 dígitos numéricos.");
                window.scrollTo(0, 0);
                return;
            }
        }

        // Validar carga de documentos en primer registro
        if (!isEditing && (!docs.constanciaFiscal || !docs.identificacionOficial)) {
            setError("Debes subir ambos documentos legales obligatorios (CSF e INE) para validación de seguridad.");
            window.scrollTo(0, 0);
            return;
        }

        if (scheduleEnabled && scheduleDays.length === 0) {
            setError("Debes seleccionar al menos un día de horario de atención o desactivar el límite de horario.");
            window.scrollTo(0, 0);
            return;
        }

        setLoading(true);

        try {
            const uploadDoc = async (file, pathName) => {
                if (!file) return null;
                const cleanFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
                const storageRef = ref(storage, `documentos_proveedores/${currentUser.uid}/${pathName}_${Date.now()}_${cleanFileName}`);
                const snapshot = await uploadBytes(storageRef, file);
                return await getDownloadURL(snapshot.ref);
            };

            const urlConstanciaNew = await uploadDoc(docs.constanciaFiscal, 'constancia_fiscal');
            const urlIneNew = await uploadDoc(docs.identificacionOficial, 'identificacion_oficial');

            const finalUrlConstancia = urlConstanciaNew || existingDocs.constanciaFiscalUrl;
            const finalUrlIne = urlIneNew || existingDocs.identificacionOficialUrl;

            let ubicacionFinal = {};
            if (formData.tieneLocal) {
                ubicacionFinal = {
                    tipo: 'local',
                    calle: formData.calle,
                    numeroExterior: formData.numeroExterior,
                    colonia: formData.colonia,
                    codigoPostal: formData.codigoPostal,
                    ciudad: formData.ciudad,
                    estado: formData.estado
                };
            } else {
                ubicacionFinal = {
                    tipo: 'residente',
                    referencia: 'Usar dirección de perfil de residente'
                };
            }

            const providerIdToUse = (isEditing && userData?.proveedorId) ? userData.proveedorId : currentUser.uid;
            const proveedorRef = doc(db, "market_proveedores", providerIdToUse);

            const nuevosDocumentosSubidos = !!urlConstanciaNew || !!urlIneNew;

            let galleryUrls = [];
            if (docs.fotosGaleria && docs.fotosGaleria.length > 0) {
                const uploadPromises = Array.from(docs.fotosGaleria).map(file => uploadDoc(file, 'galeria'));
                galleryUrls = await Promise.all(uploadPromises);
            }

            const finalGalleryUrls = [...(existingDocs.fotosGaleriaUrls || []), ...galleryUrls];

            const providerData = {
                nombrePublico: formData.nombrePublico,
                categoria: formData.categoria,
                descripcion: formData.descripcion,
                costoVisita: Number(formData.costoVisita),
                rangoPrecios: formData.rangoPrecios || '$',
                especialidades: formData.especialidadesInput ? formData.especialidadesInput.split(',').map(s => s.trim()).filter(s => s) : [],
                horario: {
                    habilitado: scheduleEnabled,
                    horaInicio: scheduleStart,
                    horaFin: scheduleEnd,
                    dias: scheduleDays
                },
                rfc: formData.rfc ? formData.rfc.toUpperCase() : '',
                radioCobertura: Number(formData.radioCobertura),
                ...(isEditing ? {} : {
                    ratingPromedio: 0,
                    totalCalificaciones: 0,
                    logoUrl: userData.fotoPerfilUrl || '',
                    fechaRegistro: Timestamp.now(),
                    usuarioId: currentUser.uid,
                    condominioId: userData.condominioId
                }),
                fotosGaleria: finalGalleryUrls,
                nombreContacto: formData.nombreContacto,
                telefonoWhatsapp: waClean,
                sitioWeb: formData.sitioWeb,
                redesSociales: {
                    facebook: formData.facebook,
                    instagram: formData.instagram
                },
                ubicacion: ubicacionFinal,
                documentos: {
                    constanciaFiscalUrl: finalUrlConstancia,
                    identificacionOficialUrl: finalUrlIne
                },
                ...((!isEditing || nuevosDocumentosSubidos) ? {
                    estatus: userData.condominioId ? "en_revision" : "en_revision_superuser"
                } : {})
            };

            if (isEditing) {
                await updateDoc(proveedorRef, providerData);
                if (nuevosDocumentosSubidos) {
                    showCustomAlert(
                        "¡Documentos Actualizados!",
                        "Tu perfil ha pasado a revisión nuevamente para validar tu documentación.",
                        "success",
                        () => navigate('/home')
                    );
                } else {
                    showCustomAlert(
                        "¡Perfil Actualizado!",
                        "Tu información profesional se ha guardado correctamente.",
                        "success",
                        () => navigate('/home')
                    );
                }
            } else {
                await setDoc(proveedorRef, providerData);
                showCustomAlert(
                    "¡Registro Enviado!",
                    "¡Solicitud enviada con éxito! Un administrador validará tu información antes de publicar.",
                    "success",
                    () => navigate('/home')
                );
            }

        } catch (err) {
            console.error("Error al guardar proveedor:", err);
            if (err.code === 'storage/unauthorized') {
                setError("Error de permisos al subir documentos. Verifica tu conexión o intenta de nuevo.");
            } else {
                setError("Hubo un error al guardar: " + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    if (fetchingData) return <div className="register-container"><p>Cargando datos...</p></div>;

    return (
        <div className="register-container">
            {/* Premium Header Banner */}
            <div style={{ background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)', position: 'relative', overflow: 'hidden', padding: '25px', borderRadius: '15px', marginBottom: '30px', boxShadow: '0 4px 15px rgba(190, 24, 93, 0.2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', zIndex: 2, position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <button onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(5px)', border: 'none', borderRadius: '50%', width: '42px', height: '42px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: 'background 0.2s', color: 'white', flexShrink: 0 }}>
                            <IoArrowBack size={22} />
                        </button>
                        <div style={{ marginTop: '0' }}>
                            <h1 style={{ color: 'white', margin: '0 0 5px', fontSize: '1.6rem', fontWeight: '800' }}>{isEditing ? 'Editar Perfil Profesional' : 'Registro de Profesional'}</h1>
                            <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.9rem', fontWeight: '500' }}>Directorio B2B del Marketplace</p>
                        </div>
                    </div>
                </div>
                {/* Decoración de fondo */}
                <div style={{ position: 'absolute', right: '-25px', bottom: '-40px', zIndex: 0, pointerEvents: 'none', transform: 'rotate(-10deg)', opacity: 0.15 }}>
                    <IoBriefcaseOutline size={180} color="white" />
                </div>
            </div>

            {!isEditing && (
                <div className="register-alert-banner">
                    <span className="register-alert-title">¡Bienvenido al Marketplace!</span>
                    Completa tu perfil profesional para ser visible a todos los residentes. Un administrador validará tu información antes de publicar.
                </div>
            )}

            {error && (
                <div className="reg-error-msg">
                    <IoAlertCircleOutline size={24} />
                    <span>{error}</span>
                </div>
            )}

            <form onSubmit={handleSubmit}>

                {/* --- 1. DATOS DEL NEGOCIO --- */}
                <div className="register-section-card">
                    <div className="section-title">
                        <IoBriefcaseOutline className="section-icon" />
                        1. Datos del Negocio
                    </div>

                    <div className="reg-form-group">
                        <label className="reg-label">Nombre Comercial</label>
                        <input
                            type="text"
                            name="nombrePublico"
                            value={formData.nombrePublico}
                            onChange={handleChange}
                            className="reg-input"
                            placeholder="Ej. Plomería Express"
                            required
                        />
                    </div>

                    <div className="reg-grid-2">
                        <div className="reg-form-group">
                            <label className="reg-label">Categoría Principal</label>
                            <select name="categoria" value={formData.categoria} onChange={handleChange} className="reg-select">
                                {categoriasBase.length === 0 && <option value="Plomería">Cargando...</option>}
                                {categoriasBase.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                        </div>
                        <div className="reg-form-group">
                            <label className="reg-label">Costo Base Visita ($)</label>
                            <input
                                type="number"
                                name="costoVisita"
                                value={formData.costoVisita}
                                onChange={handleChange}
                                className="reg-input"
                                placeholder="Ej. 150"
                                required
                            />
                        </div>
                    </div>

                    <div className="reg-grid-2">
                        <div className="reg-form-group">
                            <label className="reg-label">RFC del Negocio (Opcional)</label>
                            <input
                                type="text"
                                name="rfc"
                                value={formData.rfc}
                                onChange={handleChange}
                                className="reg-input"
                                placeholder="Ej. XAXX010101000"
                                maxLength="13"
                            />
                        </div>
                        <div className="reg-form-group">
                            <label className="reg-label">Rango de Precios</label>
                            <select name="rangoPrecios" value={formData.rangoPrecios} onChange={handleChange} className="reg-select">
                                <option value="$">Económico ($)</option>
                                <option value="$$">Estándar ($$)</option>
                                <option value="$$$">Premium ($$$)</option>
                            </select>
                        </div>
                    </div>

                    <div className="reg-form-group">
                        <label className="reg-label">Especialidades (Tags)</label>
                        <input
                            type="text"
                            name="especialidadesInput"
                            value={formData.especialidadesInput || ''}
                            onChange={handleChange}
                            className="reg-input"
                            placeholder="Ej. Gas, Fugas, Boiler (Separa con comas)"
                        />
                    </div>

                    <div className="reg-form-group">
                        <label className="reg-label">Descripción del Servicio</label>
                        <textarea
                            name="descripcion"
                            value={formData.descripcion}
                            onChange={handleChange}
                            className="reg-textarea"
                            placeholder="Describe qué haces, tu experiencia, garantías que ofreces..."
                            required
                        />
                    </div>

                    <div className="reg-form-group">
                        <label className="reg-label">Galería de Trabajos (Opcional)</label>
                        <label className="reg-upload-area">
                            <input
                                type="file"
                                multiple
                                hidden
                                accept="image/*"
                                onChange={(e) => handleFileChange(e, 'fotosGaleria')}
                            />
                            <div className="upload-icon-large"><IoCloudUploadOutline /></div>
                            <div className="upload-instruction">Haz clic o arrastra fotos aquí</div>
                            <small style={{ display: 'block', marginTop: '5px' }}>Sube fotos de tus mejores trabajos (Máximo 5MB por archivo)</small>
                            {docs.fotosGaleria && <div style={{ color: '#2563eb', marginTop: '10px', fontWeight: 'bold' }}>{docs.fotosGaleria.length} archivos seleccionados</div>}
                        </label>

                        {/* Galería Existente */}
                        {isEditing && existingDocs.fotosGaleriaUrls && existingDocs.fotosGaleriaUrls.length > 0 && (
                            <div style={{ marginTop: '15px' }}>
                                <div className="reg-label" style={{ fontSize: '0.9rem' }}>Fotos actuales:</div>
                                <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' }}>
                                    {existingDocs.fotosGaleriaUrls.map((url, idx) => (
                                        <img key={idx} src={url} alt="Trabajo" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* --- 2. HORARIO DE ATENCIÓN --- */}
                <div className="register-section-card">
                    <div className="section-title">
                        <IoTimeOutline className="section-icon" />
                        2. Horario de Atención
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ paddingRight: '10px' }}>
                                <strong style={{ display: 'block', fontSize: '0.95rem', color: '#0f172a', marginBottom: '4px', textAlign: 'left' }}>Limitar Horario de Atención</strong>
                                <span style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', lineHeight: '1.4', textAlign: 'left' }}>Fuera de horario no recibirás solicitudes ni llamadas fuera del rango.</span>
                            </div>
                            <div
                                onClick={() => setScheduleEnabled(!scheduleEnabled)}
                                style={{
                                    width: '50px',
                                    height: '28px',
                                    borderRadius: '15px',
                                    background: scheduleEnabled ? '#7B61FF' : '#cbd5e1',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                    flexShrink: 0
                                }}
                            >
                                <div
                                    style={{
                                        width: '22px',
                                        height: '22px',
                                        borderRadius: '50%',
                                        background: 'white',
                                        position: 'absolute',
                                        top: '3px',
                                        left: scheduleEnabled ? '25px' : '3px',
                                        transition: 'left 0.2s',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    }}
                                />
                            </div>
                        </div>

                        {scheduleEnabled && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div>
                                        <span className="reg-label" style={{ fontSize: '0.85em', color: '#666' }}>Hora Inicio</span>
                                        <input
                                            type="time"
                                            className="reg-input"
                                            style={{ padding: '8px 12px', fontSize: '0.95rem', boxSizing: 'border-box' }}
                                            value={scheduleStart}
                                            onChange={(e) => setScheduleStart(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <span className="reg-label" style={{ fontSize: '0.85em', color: '#666' }}>Hora Fin</span>
                                        <input
                                            type="time"
                                            className="reg-input"
                                            style={{ padding: '8px 12px', fontSize: '0.95rem', boxSizing: 'border-box' }}
                                            value={scheduleEnd}
                                            onChange={(e) => setScheduleEnd(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <span className="reg-label" style={{ display: 'block', marginBottom: '8px', fontSize: '0.85em', color: '#666' }}>Días de Atención</span>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {[
                                            { label: 'L', value: 1 },
                                            { label: 'M', value: 2 },
                                            { label: 'M', value: 3 },
                                            { label: 'J', value: 4 },
                                            { label: 'V', value: 5 },
                                            { label: 'S', value: 6 },
                                            { label: 'D', value: 7 },
                                        ].map((day) => {
                                            const isActive = scheduleDays.includes(day.value);
                                            return (
                                                <button
                                                    type="button"
                                                    key={day.value}
                                                    onClick={() => {
                                                        if (isActive) {
                                                            setScheduleDays(scheduleDays.filter((d) => d !== day.value));
                                                        } else {
                                                            setScheduleDays([...scheduleDays, day.value].sort());
                                                        }
                                                    }}
                                                    style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        borderRadius: '50%',
                                                        border: 'none',
                                                        background: isActive ? '#7B61FF' : '#f1f5f9',
                                                        color: isActive ? 'white' : '#64748b',
                                                        fontWeight: 'bold',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        fontSize: '0.85rem',
                                                    }}
                                                >
                                                    {day.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* --- 3. CONTACTO --- */}
                <div className="register-section-card">
                    <div className="section-title">
                        <IoPersonOutline className="section-icon" />
                        3. Contacto y Redes
                    </div>

                    <div className="reg-form-group">
                        <label className="reg-label">Nombre del Contacto</label>
                        <input type="text" name="nombreContacto" value={formData.nombreContacto} onChange={handleChange} className="reg-input" required />
                    </div>
                    <div className="reg-form-group">
                        <label className="reg-label">WhatsApp de Negocio (10 dígitos)</label>
                        <input type="tel" name="telefonoWhatsapp" value={formData.telefonoWhatsapp} onChange={handleChange} className="reg-input" placeholder="Ej. 5512345678" required />
                    </div>

                    <div className="reg-grid-2">
                        <div className="reg-form-group">
                            <label className="reg-label">Sitio Web (Opcional)</label>
                            <input type="url" name="sitioWeb" value={formData.sitioWeb} onChange={handleChange} className="reg-input" placeholder="https://" />
                        </div>
                        <div className="reg-form-group">
                            <label className="reg-label">Facebook (Opcional)</label>
                            <input type="url" name="facebook" value={formData.facebook} onChange={handleChange} className="reg-input" placeholder="Ej. https://facebook.com/tunegocio" />
                        </div>
                    </div>
                    <div className="reg-form-group">
                        <label className="reg-label">Instagram (Opcional)</label>
                        <input type="url" name="instagram" value={formData.instagram} onChange={handleChange} className="reg-input" placeholder="Ej. https://instagram.com/tunegocio" />
                    </div>
                </div>

                {/* --- 4. UBICACIÓN --- */}
                <div className="register-section-card">
                    <div className="section-title">
                        <IoMapOutline className="section-icon" />
                        4. Ubicación y Cobertura
                    </div>

                    {/* BUSCADOR DE DIRECCIÓN AUTOCOMPLETADO (GOOGLE MAPS) */}
                    <div className="reg-form-group" style={{ marginBottom: '25px' }}>
                        <label className="reg-label" style={{ fontWeight: '700', color: '#7B61FF' }}>
                            Buscar Dirección (Google Maps Premium)
                        </label>
                        <input
                            type="text"
                            id="search-address-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="reg-input"
                            placeholder="Empieza a escribir tu dirección y selecciona de la lista (Ej. Av Amaranto 441, Zapopan...)"
                            style={{ 
                                borderColor: searchLimitExceeded ? '#ef4444' : '#7B61FF', 
                                borderWidth: '2px', 
                                borderRadius: '12px',
                                background: searchLimitExceeded ? '#fef2f2' : '#ffffff'
                            }}
                            disabled={searchLimitExceeded}
                        />
                        {searchLimitExceeded && (
                            <div style={{
                                marginTop: '10px',
                                padding: '12px',
                                background: '#fef2f2',
                                border: '1px solid #fee2e2',
                                borderRadius: '8px',
                                color: '#b91c1c',
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <IoAlertCircleOutline size={18} />
                                <span>Límite de búsquedas de dirección alcanzado. Puedes posicionar el pin en el mapa o escribir la dirección manualmente.</span>
                            </div>
                        )}
                    </div>

                    <div className="reg-form-group" style={{ background: '#f9fafb', padding: '15px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                        <label className="toggle-switch" style={{ width: '100%', minWidth: '100%', display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <input
                                type="checkbox"
                                className="toggle-input"
                                name="tieneLocal"
                                checked={formData.tieneLocal}
                                onChange={handleChange}
                                style={{ flexShrink: 0 }}
                            />
                            <span style={{ fontWeight: '600', color: '#374151', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>¿Tienes un local abierto al público?</span>
                        </label>
                    </div>

                    {formData.tieneLocal && (
                        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                            <div className="reg-form-group">
                                <label className="reg-label">Calle y Número</label>
                                <input type="text" name="calle" value={formData.calle} onChange={handleChange} className="reg-input" required={formData.tieneLocal} />
                            </div>
                            <div className="reg-grid-2">
                                <div className="reg-form-group">
                                    <label className="reg-label">Colonia</label>
                                    <input type="text" name="colonia" value={formData.colonia} onChange={handleChange} className="reg-input" required={formData.tieneLocal} />
                                </div>
                                <div className="reg-form-group">
                                    <label className="reg-label">Ciudad</label>
                                    <input type="text" name="ciudad" value={formData.ciudad} onChange={handleChange} className="reg-input" required={formData.tieneLocal} />
                                </div>
                            </div>
                            <div className="reg-grid-2">
                                <div className="reg-form-group">
                                    <label className="reg-label">Estado</label>
                                    <input type="text" name="estado" value={formData.estado} onChange={handleChange} className="reg-input" required={formData.tieneLocal} />
                                </div>
                                <div className="reg-form-group">
                                    <label className="reg-label">C.P. (5 dígitos)</label>
                                    <input type="text" name="codigoPostal" value={formData.codigoPostal} onChange={handleChange} className="reg-input" required={formData.tieneLocal} maxLength="5" />
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{ marginTop: '15px' }}>
                        <div className="reg-form-group" style={{ background: '#f0f9ff', padding: '15px', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                            <label className="reg-label" style={{ color: '#0369a1', display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span>Radio de Cobertura de Servicio</span>
                                <strong>{formData.radioCobertura} km</strong>
                            </label>
                            <input
                                type="range"
                                name="radioCobertura"
                                min="2"
                                max="40"
                                step="2"
                                value={formData.radioCobertura}
                                onChange={handleChange}
                                style={{ width: '100%', accentColor: '#0284c7', cursor: 'pointer' }}
                            />
                            <small style={{ display: 'block', color: '#0284c7', marginTop: '6px', fontSize: '0.8rem' }}>
                                Indica la distancia máxima que estás dispuesto a recorrer para atender servicios.
                            </small>
                        </div>

                        {!formData.tieneLocal && (
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '15px', background: '#f8fafc', color: '#64748b', borderRadius: '12px', marginTop: '15px', border: '1px solid #e2e8f0', marginBottom: '15px' }}>
                                <div style={{flexShrink: 0}}><IoLocationOutline size={24} /></div>
                                <span style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>Se usará tu dirección de residente como referencia interna para el radio de cobertura.</span>
                            </div>
                        )}

                        {/* MAP DISPLAY AREA */}
                        <div style={{ marginTop: '20px' }}>
                            <label className="reg-label">Área de Cobertura Visual</label>
                            <div id="map-cobertura" style={{ width: '100%', height: '280px', borderRadius: '12px', border: '1px solid #cbd5e1', overflow: 'hidden', position: 'relative' }}>
                                <div className="map-loading-overlay" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(255,255,255,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, color: '#64748b', fontSize: '0.9rem' }}>
                                    Cargando mapa interactivo...
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- 5. DOCUMENTACIÓN --- */}
                <div className="register-section-card">
                    <div className="section-title">
                        <IoDocumentTextOutline className="section-icon" />
                        5. Documentación Legal
                    </div>
                    <p style={{ marginBottom: '20px', color: '#666' }}>
                        Para garantizar la seguridad de la comunidad, necesitamos validar tu identidad. Estos documentos son estrictamente confidenciales.
                    </p>

                    <div className="reg-form-group">
                        <label className="reg-label">Constancia de Situación Fiscal (PDF/Imagen, Máx. 5MB)</label>

                        {isEditing && existingDocs.constanciaFiscalUrl && (
                            <a href={existingDocs.constanciaFiscalUrl} target="_blank" rel="noopener noreferrer" className="existing-file-link">
                                <IoCheckmarkCircle /> Archivo Cargado (Ver)
                            </a>
                        )}

                        <label className="reg-upload-area" style={{ padding: '20px' }}>
                            <input type="file" hidden onChange={(e) => handleFileChange(e, 'constanciaFiscal')} accept=".pdf,image/*" />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                <IoCloudUploadOutline size={24} />
                                <span style={{ fontWeight: '500' }}>{docs.constanciaFiscal ? docs.constanciaFiscal.name : "Subir Archivo"}</span>
                            </div>
                        </label>
                    </div>

                    <div className="reg-form-group">
                        <label className="reg-label">Identificación Oficial - INE/Pasaporte (PDF/Imagen, Máx. 5MB)</label>

                        {isEditing && existingDocs.identificacionOficialUrl && (
                            <a href={existingDocs.identificacionOficialUrl} target="_blank" rel="noopener noreferrer" className="existing-file-link">
                                <IoCheckmarkCircle /> Archivo Cargado (Ver)
                            </a>
                        )}

                        <label className="reg-upload-area" style={{ padding: '20px' }}>
                            <input type="file" hidden onChange={(e) => handleFileChange(e, 'identificacionOficial')} accept=".pdf,image/*" />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                <IoCloudUploadOutline size={24} />
                                <span style={{ fontWeight: '500' }}>{docs.identificacionOficial ? docs.identificacionOficial.name : "Subir Archivo"}</span>
                            </div>
                        </label>
                    </div>
                </div>

                <button type="submit" className="reg-submit-btn" disabled={loading}>
                    {loading ? (isEditing ? 'Guardando Cambios...' : 'Enviando Solicitud...') : (isEditing ? 'Guardar Cambios y Actualizar Perfil' : 'Enviar Solicitud de Registro')}
                </button>

            </form>

            {/* MODAL DE CONFIRMACIÓN PREMIUM PERSONALIZADO */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.75)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                    animation: 'fadeIn 0.25s ease-out'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '24px',
                        padding: '35px 30px',
                        maxWidth: '420px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        border: '1px solid rgba(255,255,255,0.8)',
                        transform: 'scale(1)',
                        animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Decoración superior con gradiente de color según el tipo */}
                        <div style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0,
                            height: '6px',
                            background: modalConfig.type === 'success' 
                                ? 'linear-gradient(90deg, #10b981, #059669)' 
                                : modalConfig.type === 'error' 
                                    ? 'linear-gradient(90deg, #ef4444, #dc2626)' 
                                    : 'linear-gradient(90deg, #f59e0b, #d97706)'
                        }} />

                        {/* Icono animado */}
                        <div style={{
                            width: '76px',
                            height: '76px',
                            borderRadius: '50%',
                            background: modalConfig.type === 'success' ? '#ecfdf5' : modalConfig.type === 'error' ? '#fef2f2' : '#fffbeb',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            margin: '0 auto 20px',
                            border: `2px solid ${modalConfig.type === 'success' ? '#34d399' : modalConfig.type === 'error' ? '#f87171' : '#fbbf24'}`,
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)'
                        }}>
                            {modalConfig.type === 'success' ? (
                                <IoCheckmarkCircle size={44} color="#10b981" />
                            ) : (
                                <IoAlertCircleOutline size={44} color={modalConfig.type === 'error' ? '#ef4444' : '#f59e0b'} />
                            )}
                        </div>

                        {/* Títulos y textos */}
                        <h3 style={{
                            margin: '0 0 10px',
                            fontSize: '1.4rem',
                            fontWeight: '800',
                            color: '#1e293b'
                        }}>{modalConfig.title}</h3>
                        
                        <p style={{
                            margin: '0 0 25px',
                            fontSize: '0.95rem',
                            lineHeight: '1.5',
                            color: '#64748b',
                            fontWeight: '500'
                        }}>{modalConfig.message}</p>

                        {/* Botón de acción */}
                        <button
                            onClick={() => {
                                setShowModal(false);
                                if (modalConfig.onConfirm) {
                                    modalConfig.onConfirm();
                                }
                            }}
                            style={{
                                width: '100%',
                                padding: '14px 20px',
                                background: modalConfig.type === 'success' 
                                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                                    : modalConfig.type === 'error' 
                                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
                                        : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '16px',
                                fontSize: '0.95rem',
                                fontWeight: '700',
                                cursor: 'pointer',
                                boxShadow: '0 8px 16px -4px rgba(16, 185, 129, 0.3)',
                                transition: 'all 0.2s ease-out'
                            }}
                        >
                            Aceptar y Continuar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RegistroProveedorScreen;
